import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, LogOut, ArrowLeft } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { targetFor } from "../pages/LoginPage";

export const AdminShell = ({ roles = ["admin"], title, subtitle, children, testId = "admin-module" }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hz_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy" data-testid={`${testId}-loading`}>
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-hz-bg px-4 text-center" data-testid={`${testId}-no-perms`}>
        <p className="text-lg font-bold text-hz-navy">Tu rol no tiene acceso a este módulo.</p>
        <p className="mt-1 text-sm text-hz-ink/60">Rol actual: {user.role}</p>
        <div className="mt-4 flex gap-3">
          <Button onClick={() => navigate(targetFor(user.role))} data-testid={`${testId}-back-admin-btn`} className="bg-hz-navy font-bold text-white">Ir al panel</Button>
          <Button onClick={() => { localStorage.removeItem("hz_admin_token"); navigate("/login"); }} variant="outline" data-testid={`${testId}-relogin-btn`} className="font-semibold">Cerrar sesión</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hz-bg" data-testid={testId}>
      <header className="border-b border-hz-gray/50 bg-white print:hidden">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid={`${testId}-logo-link`}><Logo variant="title" className="h-8" /></Link>
            <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-xs font-bold text-hz-navy">{title}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-medium text-hz-ink/70 sm:block">{user.email}</span>
            <Button variant="outline" size="sm" onClick={() => navigate(targetFor(user.role))} data-testid={`${testId}-back-btn`} className="font-semibold">
              <ArrowLeft className="mr-1 h-4 w-4" /> Panel
            </Button>
            <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem("hz_admin_token"); navigate("/login"); }} data-testid={`${testId}-logout-btn`} className="font-semibold text-hz-error">
              <LogOut className="mr-1 h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {subtitle && <p className="mb-6 text-sm text-hz-ink/70">{subtitle}</p>}
        {children(user)}
      </main>
    </div>
  );
};
